import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiXCircle } from 'react-icons/fi';

export interface OptionType {
  key: any;
  value: string;
}

interface SingleSelectDropdownProps {
  label: any;
  icon?: React.ReactNode;
  options: OptionType[];
  selected: any; // Single value instead of an array
  onChange: (value: any) => void;
  className?: string;
  initiator?: (clickCall: any) => any;
  maxWidth?: any;
}

const SingleSelectDropdown: React.FC<SingleSelectDropdownProps> = ({
  label,
  icon,
  options,
  selected,
  onChange,
  className = '',
  initiator,
  maxWidth
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropUp, setDropUp] = useState(false);

  const filteredOptions = options.filter(o =>
    o.value.toLowerCase().includes(search.toLowerCase())
  );

  const selectValue = (key: any) => {
    // Select the value and close the dropdown
    onChange(key);
    setOpen(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setOpen((prev) => {
      const newState = !prev;

      if (newState && dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < 160 && spaceAbove > 160) {
          setDropUp(true);
        } else {
          setDropUp(false);
        }
      }

      return newState;
    });
  };

  return (
    <div ref={dropdownRef} className={`position-relative ${className}`}>
      {
        (typeof initiator != 'undefined') ?
          initiator(() => {
            toggleDropdown();
          }) :
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            onClick={() => toggleDropdown()}
          >
            {icon} {label}
          </button>
      }
      <AnimatePresence>
        {open && (
          <motion.div
            className="position-absolute bg-theme-card border rounded shadow-sm py-2 mt-1"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            style={{
              zIndex: 1000,
              maxHeight: '250px',
              overflowY: 'auto',
              minWidth: (typeof maxWidth != 'undefined') ? maxWidth : '220px',
              top: dropUp ? 'auto' : '100%',
              bottom: dropUp ? '100%' : 'auto',
              marginTop: dropUp ? undefined : '4px',
              marginBottom: dropUp ? '4px' : undefined,
            }}
          >
            <div className="d-flex justify-content-between align-items-center px-2 mb-2">
              <input
                type="text"
                className="form-control me-2"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {(search) && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger p-0"
                  title="Clear search"
                  onClick={() => {
                    setSearch('');
                  }}
                >
                  <FiXCircle size={18} />
                </button>
              )}
            </div>
            {filteredOptions.length > 0 ? (
              filteredOptions.map(({ key, value }) => (
                <div 
                  key={key} 
                  title={value}
                  onClick={() => selectValue(key)}
                  className={`dropdown-item d-flex align-items-center py-2 text-truncate px-3 ${selected === key.toString() || selected === key ? 'active' : ''}`} 
                  style={{ maxWidth: (typeof maxWidth != 'undefined') ? maxWidth : "300px", cursor: 'pointer' }}
                >
                  <input
                    type="radio"
                    className="form-check-input me-2 m-0"
                    checked={selected === key.toString() || selected === key}
                    readOnly
                  />
                  <span className="ms-2">{value}</span>
                </div>
              ))
            ) : (
              <div className="text-muted-theme px-2">No matches</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SingleSelectDropdown;